import { MainPageBlock, MainPageConfigType } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'

import React, { useState, useRef, useEffect, useCallback } from 'react'

import { Button, Col, DatePicker, Form, Grid, InputGroup, Row, SelectPicker, Toggle } from 'rsuite'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'

import EditorItem from '../editorItem/EditorItem'

import { useFetchSingleMainPageConfig } from '@@partner:graphql/query/mainPageConfig/singleMainPageConfig.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { MainPageFormState, makeMainPageFormState, mapApiDataToState, mapStateToApiVariable } from './MainPageConfigFormState'
import { mergeLeft } from 'rambda'
import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '@@partner:helpers/notification'
import { SaveButton } from '@components/common/form/Buttons'
import { useMutateSaveMainPageConfig } from '@@partner:graphql/mutation/mainPageConfig/saveMainPageConfig.gql'
import { useMUtateSetDefaultMainPageConfig } from '@@partner:graphql/mutation/mainPageConfig/setDefaultMainPageConfig.gql'
import { MAIN_PAGE_CONFIG_TYPE_OPTIONS } from '../constants'
import { ActiveEditorState } from 'containers/marketing/mainPageConfig/MainPageConfigFormPage'


type MainPageEditorProps = {
  selectedConfigId: GQL_ID | null
  onSelectConfigId: (id: GQL_ID | null) => void
  initialType?: MainPageConfigType
  onOpenEditor?: (editor: ActiveEditorState) => void
  onSave: () => void
}

export type BlockForm = Omit<MainPageBlock, 'type'> & {
  type: string | null
}
export default function HomePageEditor ({
  selectedConfigId,
  onSelectConfigId,
  initialType,
  onOpenEditor,
  onSave
}: MainPageEditorProps) {
  const containerRef = useRef(null)
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
      useValidatableForm<MainPageFormState>(makeMainPageFormState)

  const blocks = formState?.blocks || []

  const [ saveMainPageConfig, { loading: loadingSaveMainPageConfig } ] = useMutateSaveMainPageConfig()
  const [ setAsDefault ] = useMUtateSetDefaultMainPageConfig()

  useEffect(() => {
    if (!selectedConfigId && initialType) {
      setFormState((state) => {
        if (state.type === initialType) {
          return state
        }

        return {
          ...state,
          type: initialType
        }
      })
    }
  }, [ initialType, selectedConfigId, setFormState ])

  const { data, loading, error }  = useFetchSingleMainPageConfig({
    id: selectedConfigId
  })

  useEffect(() => {
    if (!loading && data) {
      const mappedState = mapApiDataToState(data.singleMainPageConfig)
      
      // Исправляем дублированные ID блоков
      if (mappedState.blocks && mappedState.blocks.length > 0) {
        const seenIds = new Set()
        const fixedBlocks = mappedState.blocks.map((block, index) => {
          if (seenIds.has(block.id)) {
            // Генерируем новый уникальный ID для дублированного блока
            const maxId = Math.max(...mappedState.blocks.map(b => Number(b.id) || 0))
            return {
              ...block,
              id: maxId + index + 1
            }
          }
          seenIds.add(block.id)
          return block
        })
        
        mappedState.blocks = fixedBlocks
      }
      
      setFormState(mappedState)
    }
  }, [ data, loading, setFormState ])

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(formData, state))
    },
    [ setFormState ]
  )

  const handleSetDefault = useCallback(() => {
    if (data?.singleMainPageConfig.is_active && !data?.singleMainPageConfig.isDefault) {
      setAsDefault({
        variables: {
          mainPageConfigId: data.singleMainPageConfig.id
        }
      })
        .then((response) => {
          if (response?.data?.setDefaultMainPageConfig) {
            notifyOk('Установлена по умолчанию')
            setFormState((state) =>
              mergeLeft(state, {
                isDefault: response.data?.setDefaultMainPageConfig.isDefault,
              })
            )
          }
        })
        .catch((error) => {
          notifyError('Ошибка установки по умолчанию')
          makeGraphQLErrorNotification('Ошибка установки по умолчанию')
        })
    }
  }, [ data?.singleMainPageConfig.id, data?.singleMainPageConfig.isDefault, data?.singleMainPageConfig.is_active, setAsDefault, setFormState ])

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveMainPageConfig({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then((response) => {
            if (response?.data?.updateMainPageConfig) {
              try {
                onSave()
              }
              catch (e) {
                console.error(e)
              }
              notifyOk('Настройки главной страницы сохранены')
              setFormState((state) =>
                mergeLeft(state, {
                  id: response.data?.updateMainPageConfig.id,
                })
              )
              onSelectConfigId(response.data?.updateMainPageConfig.id)
            }
          })
          .catch((error) => {
            notifyError('Ошибка сохранения')
            makeGraphQLErrorNotification('Ошибка сохранения')
          })
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, saveMainPageConfig, onSave, setFormState, onSelectConfigId ]
  )

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleEditBlocks = (blocks: BlockForm[]) => {
    setFormState((state) => {
      return {
        ...state,
        blocks: blocks.map((block, index) => {
          return {
            ...block,
            order: index + 1
          }
        }
        )
      }
    })
  }

  const handleAddBlock = () => {
    // Генерируем уникальный ID на основе максимального существующего ID
    const maxId = formState.blocks.length > 0 
      ? Math.max(...formState.blocks.map(block => Number(block.id) || 0))
      : 0
    const newId = maxId + 1
    
    handleEditBlocks([ 
      ...formState.blocks, 
      {
        id: newId,
        type: null,
        order: formState.blocks.length + 1,
        payload: null,
      } 
    ])
  }

  const handleEditBlock = (block: BlockForm) => {
    const newBlocks = blocks.map((b) => {
      if (b.id === block.id) {
        return block
      }
      return b
    })
    handleEditBlocks(newBlocks)
  }

  const handleDeleteBlock = (blockId: GQL_ID) => {
    const newBlocks = blocks.filter((b) => b.id !== blockId)
    handleEditBlocks(newBlocks)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && formState) {
      const items = formState.blocks
      const oldIndex = items.findIndex((i) => i.id === active.id)
      const newIndex = items.findIndex((i) => i.id === over?.id)
      
      // Проверяем, что оба элемента найдены
      if (oldIndex !== -1 && newIndex !== -1) {
        const newBlocks = arrayMove(items, oldIndex, newIndex)
        handleEditBlocks(newBlocks)
      }
    }
  }

  return (
    <div>
      <Form
        formValue={formState}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onCheck={handleFormError}
        ref={setFormRef}
        fluid
      >
        <Grid fluid>
          <Row>
            <Col xs={24}>
              <Form.Group>
                <Form.ControlLabel>Название</Form.ControlLabel>
                <Form.Control name="name" placeholder="Название" />
                <Form.HelpText>
                  Техническое название
                </Form.HelpText>
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Тип главной</Form.ControlLabel>
                <SelectPicker
                  data={MAIN_PAGE_CONFIG_TYPE_OPTIONS}
                  searchable={false}
                  cleanable={false}
                  block
                  value={formState.type}
                  onChange={(nextValue) => {
                    setFormState((state) => ({
                      ...state,
                      type: (nextValue ?? state.type) as MainPageConfigType
                    }))
                  }}
                />
                <Form.HelpText>
                  Выберите канал, для которого создаётся конфигурация
                </Form.HelpText>
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Активен</Form.ControlLabel>

                <Form.Control
                  accepter={Toggle}
                  size="lg"
                  name="is_active"
                  checkedChildren="Активен"
                  unCheckedChildren="Выключен"
                  disabled={formState.isDefault}
                  checked={formState.is_active}
                  value={`${formState.is_active}`}
                  onChange={(value) =>
                    setFormState((state) => ({
                      ...state,
                      is_active: value
                    }))
                  }
                />
                <Form.HelpText>
                  {formState.isDefault
                    ? 'Настройки по умолчанию не могут быть отключены'
                    : 'Активация настроек главной страницы'}
                </Form.HelpText>
              </Form.Group>

              <Form.ControlLabel>Период действия</Form.ControlLabel>

              <InputGroup>
                <Form.Control
                  accepter={DatePicker}
                  block
                  oneTap
                  isoWeek
                  disabled={formState.isDefault}
                  appearance="subtle"
                  name="activeFrom"
                  value={formState.activeFrom}
                  format="yyyy-MM-dd HH:mm:ss"
                  placeholder="дата активации"
                />
  
                <InputGroup.Addon> до </InputGroup.Addon>
  
                <Form.Control
                  accepter={DatePicker}
                  block
                  oneTap
                  isoWeek
                  disabled={formState.isDefault}
                  appearance="subtle"
                  name="activeUntil"
                  value={formState.activeUntil}
                  format="yyyy-MM-dd HH:mm:ss"
                  placeholder="дата выключения"
                />
              </InputGroup>
              <Form.HelpText>
                {formState.isDefault
                  ? 'Настройки по умолчанию не могут быть отключены'
                  : 'Выберите период действия настроек главной страницы'}
              </Form.HelpText>
            </Col>
          </Row>
          <Row 
            className='my-5'
            ref={containerRef}
          >
            <h1>Блоки</h1>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={blocks.map((block) => block.id)}
                strategy={verticalListSortingStrategy}
              >
                {blocks.map((block) => (
                  <EditorItem 
                    key={block.id}
                    block={block} 
                    onChange={handleEditBlock}
                    onDelete={handleDeleteBlock}
                    onOpenEditor={onOpenEditor}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Button 
              appearance="primary"
              onClick={handleAddBlock}
            >
                Добавить блок
            </Button>
          </Row>
        </Grid>
        <SaveButton
          loading={loading || loadingSaveMainPageConfig}
          disabled={loading || loadingSaveMainPageConfig}
        >
          Сохранить
        </SaveButton>

        { data?.singleMainPageConfig.is_active && !data.singleMainPageConfig.isDefault ? (
          <Button
            appearance='subtle'
            color="green"
            size="md"
            className="u-btn-submit"
            onClick={handleSetDefault}
          >
            Установить по умолчанию
          </Button>
        ) : <div></div>}
      </Form>
    </div>
   
  )
}
